import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Discrete mode landing page.
 *
 * When a Musa visits this page and picks the "Solitario" disguise, we:
 *   1. Swap the <link rel="manifest"> to manifest-solitario.json
 *   2. Swap favicons + apple-touch-icon to Solitario images
 *   3. Change <title> to "Solitario Clásico"
 *   4. Store the choice in localStorage so every page keeps the disguise active
 *   5. Show the iOS/Android "Add to Home Screen" instructions
 *
 * Once installed from this page, the PWA home-screen icon and name = Solitario.
 * Launching the Solitario app opens /discreto → which, if the user has already
 * enabled the real app via PIN, takes them to PK2 Scarlett. Otherwise shows a
 * fake Solitario splash with a hidden tap sequence (tap the spade 5 times) to
 * reveal the real login.
 */
export default function Discreto() {
  const nav = useNavigate();
  const [active, setActive] = useState(false);
  const [installable, setInstallable] = useState(null);
  const [tapCount, setTapCount] = useState(0);

  useEffect(() => {
    const stored = localStorage.getItem("pk2_stealth_mode");
    if (stored === "solitario") setActive(true);

    // Capture the beforeinstallprompt event on Android/Chrome
    const handler = (e) => {
      e.preventDefault();
      setInstallable(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const enableStealth = () => {
    // Swap manifest
    let mf = document.querySelector('link[rel="manifest"]');
    if (mf) mf.setAttribute("href", "/manifest-solitario.json");

    // Swap favicon + apple-touch-icon
    document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
      .forEach((el) => el.parentNode.removeChild(el));

    const icons = [
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/solitario-16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/solitario-32.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/solitario-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/solitario-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/solitario-apple-touch.png" },
      { rel: "shortcut icon", href: "/solitario.ico" },
    ];
    icons.forEach(({ rel, type, sizes, href }) => {
      const link = document.createElement("link");
      link.rel = rel;
      if (type) link.type = type;
      if (sizes) link.setAttribute("sizes", sizes);
      link.href = href;
      document.head.appendChild(link);
    });

    document.title = "Solitario Clásico";

    localStorage.setItem("pk2_stealth_mode", "solitario");
    setActive(true);
  };

  const disableStealth = () => {
    localStorage.removeItem("pk2_stealth_mode");
    setActive(false);
    // Force a reload so index.html's real icons come back
    window.location.href = "/";
  };

  const triggerInstall = async () => {
    if (!installable) return;
    installable.prompt();
    await installable.userChoice;
    setInstallable(null);
  };

  // Hidden: tap the spade 5 times to reveal login
  const handleSpadeTap = () => {
    const next = tapCount + 1;
    setTapCount(next);
    if (next >= 5) nav("/acceder");
  };

  // Show fake Solitario splash when stealth is active AND launched from PWA
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  if (active && isStandalone) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#0B3D1E" }}
        data-testid="solitario-splash"
      >
        <div className="text-center">
          <button
            onClick={handleSpadeTap}
            className="mb-6"
            aria-label="Solitario"
            data-testid="solitario-spade"
          >
            <img src="/solitario-512.png" alt="" style={{ width: 200, height: 200 }} />
          </button>
          <h1 className="font-serif text-4xl mb-2" style={{ color: "#fff" }}>
            Solitario Clásico
          </h1>
          <p className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
            Cargando baraja…
          </p>
          {tapCount > 0 && tapCount < 5 && (
            <p className="text-xs mt-4" style={{ color: "rgba(255,255,255,0.3)" }}>
              {tapCount}/5
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="discreto-page">
      <div className="overline mb-3">Modo Discreto</div>
      <h1 className="font-serif text-5xl font-light mb-4" style={{ color: "var(--text-primary)" }}>
        Instala con un <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>disfraz</em>
      </h1>
      <p className="text-sm mb-8" style={{ color: "var(--text-secondary)" }}>
        En tu pantalla de inicio verás "Solitario Clásico" en lugar de PK2 Scarlett. Nadie sabrá qué app es.
        Para abrir la app real, toca la carta de picas <strong>5 veces seguidas</strong>.
      </p>

      <div
        className="p-6 md:p-8 mb-8 flex flex-col md:flex-row items-center gap-6"
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}
        data-testid="discreto-preview"
      >
        <img src="/solitario-192.png" alt="" style={{ width: 140, height: 140 }} />
        <div className="flex-1">
          <div className="overline mb-1" style={{ color: "var(--brand-primary)" }}>Disponible</div>
          <h2 className="font-serif text-3xl mb-2" style={{ color: "var(--text-primary)" }}>
            Solitario Clásico
          </h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
            Fondo verde tradicional, carta de picas.
          </p>

          {!active ? (
            <button
              onClick={enableStealth}
              className="btn-primary text-xs"
              data-testid="discreto-activate-btn"
            >
              Activar disfraz
            </button>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1 text-xs px-3 py-2"
                style={{ border: "1px solid var(--vip-gold)", color: "var(--vip-gold)" }}
                data-testid="discreto-active-tag"
              >
                ✓ Disfraz activo
              </span>
              {installable && (
                <button
                  onClick={triggerInstall}
                  className="btn-primary text-xs"
                  data-testid="discreto-install-btn"
                >
                  Instalar en mi pantalla
                </button>
              )}
              <button
                onClick={disableStealth}
                className="btn-secondary text-xs"
                data-testid="discreto-disable-btn"
              >
                Desactivar
              </button>
            </div>
          )}
        </div>
      </div>

      {active && (
        <div
          className="p-5 mb-6"
          style={{ background: "var(--bg-surface)", border: "1px dashed var(--vip-gold)" }}
          data-testid="discreto-instructions"
        >
          <div className="overline mb-2" style={{ color: "var(--vip-gold)" }}>Instrucciones</div>
          <ol className="text-sm space-y-2 list-decimal list-inside" style={{ color: "var(--text-secondary)" }}>
            <li><strong>iPhone/iPad:</strong> Toca el botón compartir <span style={{ color: "var(--brand-primary)" }}>⎙</span> → "Agregar a pantalla de inicio".</li>
            <li><strong>Android/Chrome:</strong> Menú (⋮) → "Instalar app" o "Agregar a pantalla de inicio".</li>
            <li>Confirma con el nombre <strong>"Solitario Clásico"</strong>.</li>
            <li>El ícono aparece con la carta de picas verde ♠.</li>
            <li>Para abrir la app real: <strong>toca la carta 5 veces seguidas</strong> en la pantalla de carga.</li>
          </ol>
        </div>
      )}

      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
        El disfraz se guarda solo en tu dispositivo. No afecta la instalación de otras personas.
      </p>
    </div>
  );
}
